import { isHttpError } from '@core/http-client'

import { type TUploadFailureParams, type TUploadFailureReason } from '../types/uploadFailureTypes'

export const UPLOAD_FAILURE_MESSAGE_LIMIT = 200
export const UPLOAD_FAILURE_URL_PLACEHOLDER = '<url>'

type TUploadFailureDetails = {
    reason: TUploadFailureReason
    errorCode?: string
    errorMessage?: string
}

const URL_PATTERN = /https?:\/\/\S+/gi
const XHR_STATUS_PATTERN = /^Error: (\d{3})\b/
const NETWORK_ERROR_NAMES = new Set(['TimeoutError', 'NetworkError'])
const NETWORK_ERROR_MESSAGES = new Set(['Network Error', 'Failed to fetch', 'Load failed'])

export const sanitizeUploadFailureMessage = (message: string) =>
    message
        .replace(URL_PATTERN, UPLOAD_FAILURE_URL_PLACEHOLDER)
        .slice(0, UPLOAD_FAILURE_MESSAGE_LIMIT)

const getReasonByStatus = (status: number): TUploadFailureReason => {
    if (status >= 500) return 'server_error'
    if (status >= 400) return 'client_error'

    return 'unknown'
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null

const readBody = async (response: Response) => {
    try {
        const body: unknown = await response.clone().json()

        return isRecord(body) ? body : {}
    } catch {
        return {}
    }
}

const pickCode = (body: Record<string, unknown>) => {
    const code = body.code ?? body.error_code

    return typeof code === 'string' || typeof code === 'number' ? String(code) : ''
}

export const buildUploadFailureParams = ({
    reason,
    errorCode = '',
    errorMessage = '',
}: TUploadFailureDetails): TUploadFailureParams => ({
    params__key: ['reason', 'error_code', 'error_message'],
    params__str_value: [reason, errorCode, sanitizeUploadFailureMessage(errorMessage)],
})

export const getUploadFailureParams = async (error: unknown): Promise<TUploadFailureParams> => {
    if (isHttpError(error)) {
        const { status } = error.response
        const body = await readBody(error.response)
        const message = typeof body.message === 'string' ? body.message : error.message

        return buildUploadFailureParams({
            reason: getReasonByStatus(status),
            errorCode: pickCode(body) || String(status),
            errorMessage: message,
        })
    }

    if (!(error instanceof Error)) {
        return buildUploadFailureParams({ reason: 'unknown' })
    }

    const xhrStatus = XHR_STATUS_PATTERN.exec(error.message)?.[1]

    if (xhrStatus) {
        return buildUploadFailureParams({
            reason: getReasonByStatus(Number(xhrStatus)),
            errorCode: xhrStatus,
            errorMessage: error.message,
        })
    }

    const isNetwork =
        NETWORK_ERROR_NAMES.has(error.name) ||
        NETWORK_ERROR_MESSAGES.has(error.message) ||
        (error instanceof TypeError && /fetch|network/i.test(error.message))

    return buildUploadFailureParams({
        reason: isNetwork ? 'network' : 'unknown',
        errorMessage: error.message,
    })
}
